﻿import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../services/apiClient';
import { motion } from 'motion/react';
import { Loader2, Sparkles, GitBranch, TrendingUp, Clock, Target, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useSocket } from '@/hooks/useSocket';
import { useT } from '../lib/useT';

interface OwnerProfile {
  synthesizedAt: string;
  memoryCount: number;
  dominantTone: string;
  frequentExpressions: string[];
  interestClusters: string[];
  formalityLevel: number;
  emotionalExpressiveness: number;
  communicationPatterns: string[];
}

interface EvolutionMutation {
  field: string;
  from: number | string | string[];
  to: number | string | string[];
  reason: string;
}

interface EvolutionStep {
  auditId?: string;
  version: string;
  timestamp: string;
  trigger: string;
  ownerProfile?: OwnerProfile;
  mutations: EvolutionMutation[];
  narrative: string;
  reverted?: boolean;
  revertedAt?: string;
}

interface EvolutionAuditEntry {
  id: string;
  status: 'active' | 'reverted';
  createdAt: string;
  revertedAt?: string;
  trigger: string;
  depth: string;
  affectedLayer: 'core' | 'growth' | 'mixed';
  reversible: boolean;
  summary: string;
  mutationFields: string[];
  reasons: string[];
  sourceMemoryCount?: number;
}

interface GrowthState {
  version: number;
  lastUpdatedAt: string;
  ownerInterests: string[];
  ownerExpressions: string[];
  communicationPatterns: string[];
}

interface EvolutionData {
  personalityId: string;
  currentVector: {
    cognitiveStyle: Record<string, number>;
    socialStyle: Record<string, number>;
  } | null;
  version: string;
  evolutionConfig: {
    plasticity: number;
    minMemoriesForEvolution: number;
    minConnectionForEvolution: number;
    cooldownMs: number;
    maxMutationsPerStep: number;
  };
  history: EvolutionStep[];
  audit?: EvolutionAuditEntry[];
  growthState?: GrowthState | null;
  evolutionFrozenAt?: string | null;
}

interface Props {
  personalityId?: string;
}

const DIM_LABELS_EN: Record<string, string> = {
  analytical: 'Analytical',
  intuitive: 'Intuitive',
  systematic: 'Systematic',
  creative: 'Creative',
  warmth: 'Warmth',
  directness: 'Directness',
  playfulness: 'Playfulness',
  formality: 'Formality',
};

const DIM_ORDER = ['analytical', 'intuitive', 'systematic', 'creative', 'warmth', 'directness', 'playfulness', 'formality'];

/** Compute 8-vertex SVG polygon points for a given set of dimension values */
function getRadarPoints(
  values: Record<string, number>,
  cx: number,
  cy: number,
  radius: number,
): { points: string; vertices: Array<{ x: number; y: number; label: string; value: number }> } {
  const vertices: Array<{ x: number; y: number; label: string; value: number }> = [];
  for (let i = 0; i < DIM_ORDER.length; i++) {
    const dim = DIM_ORDER[i];
    const angle = (Math.PI * 2 * i) / DIM_ORDER.length - Math.PI / 2;
    const val = Math.max(0.05, values[dim] || 0);
    const x = cx + radius * val * Math.cos(angle);
    const y = cy + radius * val * Math.sin(angle);
    vertices.push({ x, y, label: DIM_LABELS_EN[dim] || dim, value: values[dim] || 0 });
  }
  const points = vertices.map(v => `${v.x},${v.y}`).join(' ');
  return { points, vertices };
}

/** Grid polygon points for the background rings */
function getGridPoints(cx: number, cy: number, radius: number): string {
  return DIM_ORDER.map((_, i) => {
    const angle = (Math.PI * 2 * i) / DIM_ORDER.length - Math.PI / 2;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    return `${x},${y}`;
  }).join(' ');
}

export function PersonalityEvolution({ personalityId = 'xiaofeng' }: Props) {
  const t = useT();
  const isZh = t.langCode !== 'en';
  const ui = (zh: string, en: string) => (isZh ? zh : en);
  const DIM_LABELS: Record<string, string> = {
    analytical: t.dimAnalytical || '分析型',
    intuitive: t.dimIntuitive || '直觉型',
    systematic: t.dimSystematic || '系统型',
    creative: t.dimCreative || '创造型',
    warmth: t.dimWarmth || '温暖度',
    directness: t.dimDirectness || '直接度',
    playfulness: t.dimPlayfulness || '趣味度',
    formality: t.dimFormality || '正式度',
  };

  const [data, setData] = useState<EvolutionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedStep, setSelectedStep] = useState<number | null>(null);
  const [evolving, setEvolving] = useState(false);
  const [freezing, setFreezing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const socket = useSocket();

  const fetchEvolutionData = useCallback(() => {
    apiFetch(`/api/personality/${personalityId}/evolution`)
      .then(r => r.json())
      .then(d => { setData(d); setSelectedStep(d.history?.length > 0 ? 0 : null); })
      .catch(() => toast.error(t.failedToLoadEvolution || '加载演化数据失败'))
      .finally(() => setLoading(false));
  }, [personalityId]);

  useEffect(() => {
    fetchEvolutionData();
  }, [fetchEvolutionData]);

  // Listen for real-time evolution events via WebSocket
  // v3.12.0: 始终 return noop function 避免 React 19 严格模式检测到 useEffect return 不一致
  useEffect(() => {
    if (!socket) return () => {};
    const handler = (event: { personalityId: string; version: string; narrative: string; mutations: any[]; timestamp: string }) => {
      if (event.personalityId === personalityId) {
        toast.success(`${t.lumiEvolvedTo || ui('小枫 已演化到', '小枫 evolved to')} ${event.version}!`, {
          description: event.narrative?.slice(0, 100),
        });
        // Re-fetch to get the full updated state
        fetchEvolutionData();
      }
    };
    socket.on('personality:evolved', handler);
    return () => { socket.off('personality:evolved', handler); };
  }, [socket, personalityId, fetchEvolutionData]);

  const triggerEvolution = async () => {
    if (!window.confirm(ui(
      '确定要触发一次人格演化吗？小枫将基于最近的互动记忆进行分析，自动调整人格向量。此操作不可撤销。',
      'Trigger personality evolution? xiaofeng will analyze recent interaction memories and adjust personality vectors. This cannot be undone.'
    ))) return;
    setEvolving(true);
    try {
      const r = await apiFetch(`/api/personality/${personalityId}/evolve`, { method: 'POST' });
      if (!r.ok) throw new Error((await r.json()).error || '演化失败');
      // Re-fetch to get the full updated state with new evolution history
      const refresh = await apiFetch(`/api/personality/${personalityId}/evolution`);
      const d = await refresh.json();
      setData(d);
      setSelectedStep(d.history?.length > 0 ? d.history.length - 1 : null);
      toast.success(t.personalityEvolved || '人格已演化！');
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setEvolving(false);
    }
  };

  const toggleFreeze = async () => {
    if (!data) return;
    setFreezing(true);
    try {
      const r = await apiFetch(`/api/personality/${personalityId}/evolution/freeze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ frozen: !data.evolutionFrozenAt }),
      });
      if (!r.ok) throw new Error((await r.json()).error || '更新冻结状态失败');
      fetchEvolutionData();
      toast.success(data.evolutionFrozenAt ? ui('人格演化已恢复', 'Evolution unfrozen') : ui('人格演化已冻结', 'Evolution frozen'));
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setFreezing(false);
    }
  };

  // Bug 34: 将 selected 相关声明提升到 revertSelected 之前
  const evolutionSteps = data?.history || [];
  const selected = selectedStep !== null && selectedStep !== undefined ? evolutionSteps[selectedStep] : null;
  const selectedAudit = selected?.auditId ? data?.audit?.find(a => a.id === selected.auditId) : null;

  const revertSelected = async () => {
    if (!selected?.auditId) return;
    if (!window.confirm(ui(
      '确定要回退此演化步骤吗？此操作不可撤销。',
      'Revert this evolution step? This cannot be undone.'
    ))) return;
    setReverting(true);
    try {
      const r = await apiFetch(`/api/personality/${personalityId}/evolution/revert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auditId: selected.auditId }),
      });
      if (!r.ok) throw new Error((await r.json()).error || '回滚演化失败');
      fetchEvolutionData();
      toast.success(ui('已回退该次人格演化', 'Evolution reverted'));
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setReverting(false);
    }
  };

  // v3.12.1: constants + useMemo MUST be before early return (React hooks rule)
  const cx = 150, cy = 150, r = 130;
  const radarGrid = useMemo(() => {
    const rings = [0.25, 0.5, 0.75, 1].map(scale => (
      <motion.polygon key={scale} points={getGridPoints(cx, cy, r * scale)}
        fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth={0.5}
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: scale * 0.1 }} />
    ));
    const lines = DIM_ORDER.map((dim, i) => {
      const angle = (Math.PI * 2 * i) / DIM_ORDER.length - Math.PI / 2;
      return <line key={dim} x1={cx} y1={cy} x2={cx + r * Math.cos(angle)} y2={cy + r * Math.sin(angle)}
        stroke="rgba(255,255,255,0.10)" strokeWidth={0.6} />;
    });
    const labels = DIM_ORDER.map((dim, i) => {
      const angle = (Math.PI * 2 * i) / DIM_ORDER.length - Math.PI / 2;
      const lx = cx + (r + 22) * Math.cos(angle), ly = cy + (r + 22) * Math.sin(angle);
      return <text key={dim} x={lx} y={ly} textAnchor="middle" dominantBaseline="middle"
        className="fill-white/30" style={{ fontSize: '10px', fontWeight: 600, fontFamily: 'system-ui' }}>{DIM_LABELS[dim]}</text>;
    });
    return <>{rings}{lines}{labels}</>;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <div className="xf-panel flex h-full items-center justify-center bg-zinc-950/60">
        <Loader2 size={24} className="animate-spin text-white/55" />
      </div>
    );
  }

  const hasHistory = data && data.history.length > 0;
  // Bug 34: evolutionSteps 和 selected 已提升到 revertSelected 之前

  const vectorToMap = (vec: Record<string, number>) => {
    const m: Record<string, number> = {};
    for (const [k, v] of Object.entries(vec)) m[k] = v;
    return m;
  };

  return (
    <div className="xf-surface h-full overflow-hidden rounded-2xl bg-zinc-950/75">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/[0.08] p-5">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-fuchsia-300/15 bg-fuchsia-400/10 text-fuchsia-300">
            <GitBranch size={18} />
          </span>
          <div>
            <h2 className="text-lg font-bold text-white/90">{t.personalityEvolution || ui('人格演化', 'Personality Evolution')}</h2>
            {data && (
              <p className="text-xs text-white/55 font-mono">
                v{data.version} &middot; {evolutionSteps.length} step{evolutionSteps.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleFreeze}
            disabled={freezing}
            aria-label={data?.evolutionFrozenAt ? (isZh ? '解冻演化' : 'Unfreeze evolution') : (isZh ? '冻结演化' : 'Freeze evolution')}
            className={`xf-button h-9 px-3 text-xs ${
              data?.evolutionFrozenAt
                ? 'border-slate-500/20 bg-slate-500/10 text-slate-300'
                : ''
            }`}
          >
            {freezing ? <Loader2 size={12} className="animate-spin" /> : null}
            {data?.evolutionFrozenAt ? ui('已冻结', 'Frozen') : ui('活跃', 'Active')}
          </button>
          <button
            onClick={triggerEvolution}
            disabled={evolving || Boolean(data?.evolutionFrozenAt)}
            aria-label={isZh ? '触发人格演化' : 'Trigger personality evolution'}
            className="xf-button-primary h-9 border-fuchsia-500/30 bg-fuchsia-500/20 px-4 text-xs text-fuchsia-300 hover:bg-fuchsia-500/30"
          >
            {evolving ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
            {t.evolve || ui('演化', 'Evolve')}
          </button>
        </div>
      </div>

      {!hasHistory ? (
        <div className="flex h-64 flex-col items-center justify-center gap-4 text-white/45">
          <TrendingUp size={48} />
          <p className="text-xs">{t.noEvolutionHistory || ui('暂无演化历史。小枫 会在与你的互动中逐渐成长。', "No evolution history yet. 小枫's personality grows with you.")}</p>
        </div>
      ) : (
        <div className="flex h-[calc(100%-65px)]">
          {/* Left: Radar Chart */}
          <div className="flex w-[min(420px,92vw)] flex-shrink-0 flex-col items-center border-r border-white/[0.08] p-6">
            {/* v3.12.8: a11y */}
            <svg width={340} height={340} viewBox="0 0 300 300" role="img" aria-label={isZh ? '人格雷达图' : 'Personality Radar Chart'}>
              <title>{isZh ? '人格雷达图' : 'Personality Radar Chart'}</title>
              <defs>
                <radialGradient id="radarFill" cx="50%" cy="50%" r="60%">
                  <stop offset="0%" stopColor="rgba(192,132,252,0.45)" />
                  <stop offset="100%" stopColor="rgba(192,132,252,0.08)" />
                </radialGradient>
                <linearGradient id="radarStroke" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#c084fc" />
                  <stop offset="50%" stopColor="#a78bfa" />
                  <stop offset="100%" stopColor="#818cf8" />
                </linearGradient>
                <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
                  <feGaussianBlur stdDeviation="2.5" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>
              {/* Background rings, axes, labels (useMemo cached v3.9.2) */}
              {radarGrid}
              {/* Historical step polygons */}
              {evolutionSteps.map((step, idx) => {
                if (!data?.currentVector) return null;
                // Reconstruct vector for this step by applying mutations backwards
                // For simplicity, use current vector if not selected, or show all steps
                const opacity = idx === selectedStep ? 1 : 0.2;
                // We show the current vector for the latest step, or a faded version for older ones
                const values = data.currentVector;
                const merged: Record<string, number> = {
                  ...values.cognitiveStyle,
                  ...values.socialStyle,
                };
                // Apply reverse mutations for older steps
                for (let s = evolutionSteps.length - 1; s > idx; s--) {
                  for (const mut of evolutionSteps[s].mutations) {
                    const parts = mut.field.split('.');
                    if (parts[0] === 'personalityVector' && parts.length === 3) {
                      const style = parts[1] as 'cognitiveStyle' | 'socialStyle';
                      const dim = parts[2];
                      if (merged[dim] !== undefined && typeof mut.from === 'number') {
                        merged[dim] = mut.from;
                      }
                    }
                  }
                }
                const { points, vertices } = getRadarPoints(merged, cx, cy, r);
                return (
                  <g key={step.version}>
                    <motion.polygon
                      points={points}
                      fill={idx === selectedStep ? 'url(#radarFill)' : 'rgba(192,132,252,0.06)'}
                      stroke={idx === selectedStep ? 'url(#radarStroke)' : 'rgba(192,132,252,0.2)'}
                      strokeWidth={idx === selectedStep ? 1.5 : 0.5}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: idx * 0.1 }}
                    />
                    {idx === selectedStep && vertices.map((v, vi) => (
                      <circle key={vi} cx={v.x} cy={v.y} r={4.5} fill="rgba(192,132,252,1)" stroke="rgba(255,255,255,0.9)" strokeWidth={1} filter="url(#glow)" />
                    ))}
                  </g>
                );
              })}
            </svg>
            {selected && (
              <p className="text-[12px] text-white/55 text-center mt-2 italic max-w-[340px]">
                {selected.narrative}
              </p>
            )}
          </div>

          {/* Right: Timeline + Mutations */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Timeline */}
            <div className="custom-scrollbar overflow-x-auto border-b border-white/[0.08] p-4">
              <div className="flex gap-2 items-center min-w-max">
                <Clock size={12} className="text-white/45 flex-shrink-0" />
                {evolutionSteps.map((step, idx) => (
                  <button
                    key={step.version}
                    onClick={() => setSelectedStep(idx)}
                    aria-current={idx === selectedStep ? 'step' : undefined}
                    className={`flex shrink-0 items-center gap-2 rounded-xl border px-3 py-2 font-mono text-xs transition-colors ${
                      idx === selectedStep
                        ? 'border-fuchsia-500/30 bg-fuchsia-500/20 text-fuchsia-300'
                        : 'border-white/[0.06] bg-white/[0.04] text-white/40 hover:bg-white/[0.08]'
                    }`}
                  >
                    <span className="font-black">v{step.version}</span>
                    <span className="text-xs opacity-50">{step.timestamp.slice(0, 10)}</span>
                    <span className={`text-xs uppercase px-1.5 py-0.5 rounded-full ${
                      step.trigger === 'manual' ? 'bg-amber-500/20 text-amber-400' :
                      step.trigger === 'milestone' ? 'bg-emerald-500/20 text-emerald-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>{step.trigger}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Mutation details */}
            <div className="custom-scrollbar flex-1 space-y-4 overflow-auto p-5">
              {selected ? (
                <>
                  {/* Owner Profile (if available) */}
                  {selected.ownerProfile && (
                    <div className="xf-panel space-y-2 p-4">
                      <h3 className="text-xs font-black text-white/55 uppercase tracking-wider flex items-center gap-2">
                        <Target size={12} /> {t.ownerProfile || ui('用户画像', 'Owner Profile')}
                      </h3>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-white/45">{t.dominantTone || '主导语气：'}</span>
                          <span className="text-white/60">{selected.ownerProfile.dominantTone}</span>
                        </div>
                        <div>
                          <span className="text-white/45">{t.formalityLabel || '正式度：'}</span>
                          <span className="text-white/60">{selected.ownerProfile.formalityLevel.toFixed(1)}</span>
                        </div>
                        <div>
                          <span className="text-white/45">{t.expressiveness || '表现力：'}</span>
                          <span className="text-white/60">{selected.ownerProfile.emotionalExpressiveness.toFixed(1)}</span>
                        </div>
                        <div>
                          <span className="text-white/45">{t.memoriesAnalyzed || '已分析记忆：'}</span>
                          <span className="text-white/60">{selected.ownerProfile.memoryCount}</span>
                        </div>
                      </div>
                      {selected.ownerProfile.interestClusters?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selected.ownerProfile.interestClusters.map(c => (
                            <span key={c} className="px-2 py-0.5 bg-fuchsia-500/10 border border-fuchsia-500/20 rounded-full text-xs text-fuchsia-300">{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {selectedAudit && (
                    <div className="xf-panel space-y-3 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <h3 className="text-xs font-black text-white/55 uppercase tracking-wider">{ui('演化审计', 'Evolution Audit')}</h3>
                        <span className={`text-[11px] font-mono uppercase px-2 py-1 rounded-full ${
                          selectedAudit.status === 'reverted'
                            ? 'bg-zinc-500/20 text-zinc-300'
                            : selectedAudit.affectedLayer === 'core'
                            ? 'bg-red-500/20 text-red-300'
                            : selectedAudit.affectedLayer === 'mixed'
                            ? 'bg-amber-500/20 text-amber-300'
                            : 'bg-emerald-500/20 text-emerald-300'
                        }`}>
                          {selectedAudit.status} / {selectedAudit.affectedLayer}
                        </span>
                      </div>
                      <div className="text-[12px] text-white/50 space-y-1">
                        <div>{ui('审计 ID', 'Audit ID')}: <span className="font-mono text-white/65">{selectedAudit.id}</span></div>
                        <div>{ui('可回退', 'Reversible')}: {selectedAudit.reversible ? ui('是', 'Yes') : ui('否', 'No')}</div>
                        <div>{ui('字段', 'Fields')}: {selectedAudit.mutationFields.join(', ') || ui('无', 'none')}</div>
                      </div>
                      <button
                        onClick={revertSelected}
                        disabled={!selectedAudit.reversible || selectedAudit.status === 'reverted' || reverting}
                        className="xf-button h-9 border-red-500/20 bg-red-500/10 px-3 text-xs text-red-300 hover:bg-red-500/20"
                      >
                        {reverting ? ui('回退中...', 'Reverting...') : ui('回退这一步', 'Revert This Step')}
                      </button>
                    </div>
                  )}

                  {/* Mutations */}
                  <h3 className="text-xs font-black text-white/55 uppercase tracking-wider flex items-center gap-2">
                    <Sparkles size={12} /> {t.mutations || ui('变化记录', 'Mutations')}
                  </h3>
                  {selected.mutations.map((mut, mi) => (
                    <motion.div
                      key={mi}
                      initial={{ opacity: 0, x: 10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: mi * 0.08 }}
                      className="xf-panel space-y-2 p-4"
                    >
                      <div className="flex items-center gap-2">
                        <code className="text-[12px] font-mono text-indigo-400 bg-indigo-500/10 px-2 py-0.5 rounded">
                          {mut.field}
                        </code>
                        <ChevronRight size={10} className="text-white/35" />
                      </div>
                      <div className="flex items-center gap-3 text-xs font-mono">
                        <span className="text-red-400/60 line-through">{typeof mut.from === 'number' ? (mut.from as number).toFixed(2) : String(mut.from)}</span>
                        <span className="text-white/35">&rarr;</span>
                        <span className="text-emerald-400">{typeof mut.to === 'number' ? (mut.to as number).toFixed(2) : String(mut.to)}</span>
                      </div>
                      <p className="text-[12px] text-white/55 italic">{mut.reason}</p>
                    </motion.div>
                  ))}
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-white/35 text-xs">
                  {t.selectEvolutionStep || ui('选择一个演化步骤查看详情', 'Select an evolution step to view details')}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Progress indicator + Config bar (v3.9.2) */}
      {data && (
        <>
          <div className="border-t border-white/[0.08] px-5 py-3 space-y-2">
            {(() => {
              const cfg = data.evolutionConfig, gs = data.growthState;
              const memCount = gs?.ownerInterests?.length || 0;
              const memPct = Math.min(100, Math.round((memCount / cfg.minMemoriesForEvolution) * 100));
              const lastEvo = data.history?.[data.history.length - 1]?.timestamp;
              const daysSince = lastEvo ? Math.floor((Date.now() - new Date(lastEvo).getTime()) / 86400000) : 999;
              const cdDays = Math.round(cfg.cooldownMs / 86400000);
              const cdPct = Math.min(100, Math.round((daysSince / cdDays) * 100));
              const ready = memCount >= cfg.minMemoriesForEvolution && daysSince >= cdDays;
              return (<>
                <div className="flex items-center gap-3 text-[12px]">
                  <Target size={12} className="text-indigo-400/60" />
                  <span className="text-white/50">{ui('记忆进度', 'Memory')}: <span className="text-white/40">{memCount}/{cfg.minMemoriesForEvolution}</span></span>
                  <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: memPct + '%', background: 'linear-gradient(90deg, #818cf8, #c084fc)' }} />
                  </div>
                  <span className="text-white/30 w-8 text-right">{memPct}%</span>
                </div>
                <div className="flex items-center gap-3 text-[12px]">
                  <Clock size={12} className={ready ? 'text-emerald-400' : 'text-amber-400/60'} />
                  <span className="text-white/50">{ui('冷却', 'Cooldown')}: <span className="text-white/40">{daysSince}d / {cdDays}d</span></span>
                  <div className="flex-1 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: cdPct + '%', background: ready ? 'linear-gradient(90deg, #34d399, #a78bfa)' : 'linear-gradient(90deg, #fbbf24, #f87171)' }} />
                  </div>
                  <span className={`text-xs font-bold ${ready ? 'text-emerald-400' : 'text-amber-400'}`}>{ready ? ui('可演化', 'Ready') : ui('冷却中', 'Cooling')}</span>
                </div>
              </>);
            })()}
          </div>
        <div className="flex items-center gap-6 border-t border-white/[0.08] px-5 py-3 font-mono text-[12px] text-white/45">
          <span>{t.plasticity || '可塑性：'}<span className="text-white/40">{data.evolutionConfig.plasticity.toFixed(1)}</span></span>
          <span>{t.cooldown || '冷却：'}<span className="text-white/40">{(data.evolutionConfig.cooldownMs / 86400000).toFixed(0)}d</span></span>
          <span>{t.maxMutations || ui('最大变化数:', 'Max Mutations:')} <span className="text-white/40">{data.evolutionConfig.maxMutationsPerStep}</span></span>
          <span>{t.minMemories || '最少记忆：'}<span className="text-white/40">{data.evolutionConfig.minMemoriesForEvolution}</span></span>
        </div>
        </>
      )}
    </div>
  );
}
